import { type RefObject, useLayoutEffect, useState } from "react";
import { Button, Grow, Paper } from "@mui/material";
import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  useFloating,
} from "@floating-ui/react";
import { DROPDOWN_SURFACE } from "src/theme/dropdownSurface";

interface TimeWindowNoDataPopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  onSelectDifferentTimeWindow: () => void;
}

export default function TimeWindowNoDataPopover({
  open,
  anchorRef,
  onSelectDifferentTimeWindow,
}: TimeWindowNoDataPopoverProps) {
  const [shouldRender, setShouldRender] = useState(open);

  if (open && !shouldRender) {
    setShouldRender(true);
  }

  const { refs, floatingStyles } = useFloating({
    open: open || shouldRender,
    placement: "bottom-end",
    strategy: "fixed",
    transform: false,
    middleware: [offset(10), flip()],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    refs.setReference(anchorRef.current);
  }, [anchorRef, refs.setReference]);

  if (!shouldRender) {
    return null;
  }

  return (
    <FloatingPortal>
      <Grow
        in={open}
        unmountOnExit
        onExited={() => setShouldRender(false)}
        style={{ transformOrigin: "top right" }}
      >
        <Paper
          ref={refs.setFloating}
          elevation={8}
          style={floatingStyles}
          sx={[DROPDOWN_SURFACE, { zIndex: (theme) => theme.zIndex.appBar }]}
        >
          <div className="p-6 text-center">
            <span className="text-text-secondary">
              No data available for the the selected time-window
            </span>
            <br />
            <br />
            <Button
              variant="contained"
              onClick={onSelectDifferentTimeWindow}
              data-test-id="time-window--no-data-popover-button"
            >
              Select a different time-window
            </Button>
          </div>
        </Paper>
      </Grow>
    </FloatingPortal>
  );
}
